import {myTeammateApi} from '@/api/my-teammate.api.js'
import {ref, onUnmounted} from 'vue'

/**
 * Helper composable for teammate messages querying in the teammate chat view.
 * The conversation is resolved server-side from the current user, so there is
 * no enrollment or channel id to pass.
 */
export function useTeammateMessagesQuerying() {

  //Refs
  const conversation = ref(null)
  const messages = ref([])

  //Load messages (conversation metadata comes from the same endpoint)
  const loadMessages = async () => {
    const data = await myTeammateApi.getThread()
    conversation.value = data.conversation
    setMessages(data.messages)
  }

  //Best-effort background refresh used by polling and the visibility handler. A
  //transient failure on a 2s tick must not become an unhandled rejection (or
  //spam Sentry every tick), so swallow errors here — the next tick recovers.
  //Errors on the user-initiated initial load are still surfaced by the caller.
  const refreshMessages = () => loadMessages().catch(() => {})

  //Force update messages ref
  const updateMessages = () => {
    messages.value = [...messages.value]
  }

  //Helper to set messages after loading
  const setMessages = allMessages => {

    //If there are no messages yet, set all of them directly (skip the no-op
    //empty-to-empty assignment so an idle poll doesn't fire array watchers)
    if (messages.value.length === 0) {
      if (allMessages.length > 0) {
        messages.value = allMessages
      }
      return
    }

    //Reconcile optimistic user messages with their persisted counterparts so a
    //poll that races the create request doesn't duplicate them. The teammate
    //API doesn't echo a client correlation id (external_id persistence is
    //deferred to the channel layer — see Teammate::InboundMessageService), so we
    //match an unconfirmed (no id) incoming message to the first server message
    //with the same body that we haven't already adopted.
    let hasAdoptedMessages = false
    messages.value
      .filter(message => message.isNew && message.isIncoming)
      .forEach(message => {
        const match = allMessages.find(m =>
          m.id &&
          m.isIncoming &&
          m.body === message.body &&
          !messages.value.some(existing => existing.id === m.id)
        )
        if (match) {
          Object.assign(message, match)

          //The server copy proves delivery, so clear a stale send error (e.g.
          //the create request timed out after the backend persisted the message)
          message.error = null
          hasAdoptedMessages = true
        }
      })

    //Now find out which messages are new and append them. Only replace the array
    //(and thereby trigger watchers and re-renders downstream) when this poll
    //actually changed something — most 2s ticks return an unchanged thread.
    const existingIds = new Set(messages.value.map(message => message.id))
    const newMessages = allMessages.filter(message => !existingIds.has(message.id))
    if (newMessages.length > 0 || hasAdoptedMessages) {
      messages.value = [...messages.value, ...newMessages]
    }
  }

  //Poll messages. The assistant reply lands asynchronously (the agent runs on a
  //short server-side debounce), so a tight cadence keeps the chat feeling live
  //without a websocket. The poll is a cheap, current-user-scoped read.
  //Caller intent (shouldPollMessages) is tracked separately from the interval:
  //the visibility handler pauses/resumes only the interval, so it can never
  //resurrect polling that the caller stopped (e.g. paused during a send) nor
  //start it in a tab that mounted hidden.
  const POLL_INTERVAL_MS = 2000
  const pollMessagesInterval = ref(null)
  const shouldPollMessages = ref(false)

  //Whether the owning component is still mounted; onUnmounted only fires once,
  //so any code path that could (re)start polling must check this flag
  let isActive = true
  const setPollingInterval = () => {
    clearInterval(pollMessagesInterval.value)
    pollMessagesInterval.value = setInterval(refreshMessages, POLL_INTERVAL_MS)
  }
  const clearPollingInterval = () => {
    clearInterval(pollMessagesInterval.value)
    pollMessagesInterval.value = null
  }
  const startPollingMessages = () => {

    //No-op once unmounted: the view pauses polling across an awaited send, so
    //its resume call can land after onUnmounted already ran its one-time
    //cleanup — an interval created here would poll forever with nothing left
    //to clear it
    if (!isActive) {
      return
    }
    shouldPollMessages.value = true

    //Don't run the interval while hidden — the visibility handler starts it
    //once the tab becomes visible
    if (!document.hidden) {
      setPollingInterval()
    }
  }
  const stopPollingMessages = () => {
    shouldPollMessages.value = false
    clearPollingInterval()
  }

  //Pause polling while the tab is backgrounded: a hidden tab doesn't need live
  //updates, and leaving a 2s interval (plus its re-renders) running for hours is
  //wasted CPU. Resume with an immediate refresh when the tab comes back.
  const handleVisibilityChange = () => {
    if (document.hidden) {
      clearPollingInterval()
    }
    else if (isActive && shouldPollMessages.value) {
      refreshMessages()
      setPollingInterval()
    }
  }
  document.addEventListener('visibilitychange', handleVisibilityChange)

  //Stop polling and unbind the visibility listener on unmount. The isActive
  //flag must go false first so late resume calls (e.g. a send that resolves
  //after navigation) can't restart the interval.
  onUnmounted(() => {
    isActive = false
    stopPollingMessages()
    document.removeEventListener('visibilitychange', handleVisibilityChange)
  })

  //Expose interface
  return {
    conversation,
    messages,
    loadMessages,
    updateMessages,
    startPollingMessages,
    stopPollingMessages,
  }
}
